import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Trophy, Medal, Award, Gift } from "lucide-react";

import { specialOfferApi } from "@/lib/extensions-api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Tabs } from "@/components/ui/Tabs";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import { CreateSpecialOfferModal } from "./CreateSpecialOfferModal";
import type { ReportPeriod } from "@/types/report";

const RANK_ICONS: Record<number, any> = {
  1: { icon: Trophy, cls: "bg-yellow-100 text-yellow-700" },
  2: { icon: Medal,  cls: "bg-surface-200 text-surface-700" },
  3: { icon: Award,  cls: "bg-orange-100 text-orange-700" },
};

export function TopUsersPickerPage() {
  const [period, setPeriod] = useState<ReportPeriod>("monthly");
  const [target, setTarget] = useState<string | undefined>();
  const nav = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-so-top-users", period],
    queryFn: () => specialOfferApi.topUsers({ period, limit: 20 }),
  });

  return (
    <div className="space-y-6">
      <header>
        <button onClick={() => nav(-1)}
                 className="text-sm text-surface-500 hover:text-surface-800">
          ← Back to Special Offers
        </button>
        <h1 className="text-2xl font-bold text-surface-900 mt-2">Discover Top Users</h1>
        <p className="text-sm text-surface-500 mt-1">
          Browse top performers and grant them a Special Offer directly.
        </p>
      </header>

      <Card>
        <div className="px-5 pt-4">
          <Tabs value={period}
                 onChange={(v) => setPeriod(v as ReportPeriod)}
                 tabs={[
                   { value: "today",   label: "Today" },
                   { value: "weekly",  label: "This Week" },
                   { value: "monthly", label: "This Month" },
                   { value: "yearly",  label: "This Year" },
                 ]} />
        </div>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (data?.top_users ?? []).length === 0 ? (
            <EmptyState icon={Trophy} title="No qualifying users in this period" />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>Rank</TH>
                  <TH>User</TH>
                  <TH>Name</TH>
                  <TH className="text-right">Transactions</TH>
                  <TH className="text-right">Sales</TH>
                  <TH></TH>
                </TR>
              </THead>
              <tbody>
                {data!.top_users.map((u) => {
                  const r = RANK_ICONS[u.rank];
                  return (
                    <TR key={u.user_id}>
                      <TD>
                        {r ? (
                          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${r.cls}`}>
                            <r.icon className="h-3.5 w-3.5" />
                            <span className="font-bold">#{u.rank}</span>
                          </div>
                        ) : (
                          <span className="font-mono text-surface-500">#{u.rank}</span>
                        )}
                      </TD>
                      <TD><CopyableId value={u.user_id} /></TD>
                      <TD className="font-medium">{u.full_name}</TD>
                      <TD className="text-right">{formatNumber(u.transaction_count)}</TD>
                      <TD className="text-right font-bold text-brand-700">
                        {formatCurrency(u.total_sales)}
                      </TD>
                      <TD className="text-right">
                        <Button size="sm"
                                 leftIcon={<Gift className="h-3.5 w-3.5" />}
                                 onClick={() => setTarget(u.user_id)}>
                          Grant Offer
                        </Button>
                      </TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <CreateSpecialOfferModal
        open={!!target}
        onClose={() => setTarget(undefined)}
        prefilledUserId={target}
      />
    </div>
  );
}
